import { subDays } from 'date-fns';
import { Scatter } from 'react-chartjs-2';
import React from 'react';
import cloneDeep from 'lodash.clonedeep';

const blue = 'rgba(0, 127, 172, 1)';
const green = 'rgba(82, 174, 50, 1)';
const red = 'rgba(150,0,0, 1)';

interface ChartProps {
  chartData: Object[]
  type: 'co' | 'nox' | 'temp' | 'events'
  limits?: Object[]
  fullSize?: boolean
  showHmw?: boolean
  days: number
  maxDate?: number
  isEventChart?: boolean // New Boolean
}

function Chart({
  chartData, type, limits, fullSize = false, showHmw = false, days, maxDate, isEventChart = false
}: ChartProps) {
  // 1. CALCULATE THE MIN DATE
  // This ensures that even if the first event is in June, the chart starts on Jan 1st
  const minDate = maxDate
    ? subDays(new Date(maxDate), days).getTime()
    : undefined;

  const data = cloneDeep(chartData);
  const limitData = cloneDeep(limits);

  let color;
  let label;
  let limitLabel;
  let yAxisLabel;

  let pointSize;
  let pointHitSize;
  let unitStepSize;

  if (showHmw && fullSize) {
    if (days <= 60) {
      pointSize = 1;
      pointHitSize = 4;
      unitStepSize = 3;
    } else if (days <= 120) {
      pointSize = 1;
      pointHitSize = 4;
      unitStepSize = 5;
    } else if (days <= 270) {
      pointSize = 1;
      pointHitSize = 4;
      unitStepSize = 7;
    } else {
      pointSize = 1;
      pointHitSize = 4;
      unitStepSize = 14;
    }
  } else if (days <= 60) {
    pointSize = fullSize ? 5 : 3;
    pointHitSize = fullSize ? 7 : 5;
    unitStepSize = fullSize ? 3 : 5;
  } else if (days <= 120) {
    pointSize = fullSize ? 3 : 2;
    pointHitSize = fullSize ? 5 : 3;
    unitStepSize = fullSize ? 5 : 7;
  } else if (days <= 270) {
    pointSize = fullSize ? 2 : 1;
    pointHitSize = fullSize ? 4 : 2;
    unitStepSize = fullSize ? 7 : 14;
  } else {
    pointSize = fullSize ? 2 : 1;
    pointHitSize = fullSize ? 3 : 2;
    unitStepSize = fullSize ? 14 : 28;
  }

  switch (type) {
    case 'co':
      color = blue;
      label = (fullSize && showHmw) ? 'HMW CO' : 'TMW CO';
      limitLabel = 'Grenzwert CO';
      yAxisLabel = 'Konzentration in mg/Nm³ (trocken, 5% Sauerstoffgehalt)';
      break;
    case 'nox':
      color = green;
      label = (fullSize && showHmw) ? 'HMW NOx' : 'TMW NOx';
      limitLabel = 'Grenzwert NOx';
      yAxisLabel = 'Konzentration in mg/Nm³ (trocken, 5% Sauerstoffgehalt)';
      break;
    case 'temp':
      color = red;
      label = (fullSize && showHmw) ? 'HMW Temperatur' : 'TMW Temperatur';
      limitLabel = 'Grenzwert Temperatur';
      yAxisLabel = 'Temperatur in °C';
      break;
    default:
      color = blue;
      label = (fullSize && showHmw) ? 'HMW CO' : 'TMW CO';
      limitLabel = 'Grenzwert CO';
      yAxisLabel = 'Konzentration in mg/Nm³ (trocken, 5% Sauerstoffgehalt)';
      break;
  }

  // Define your datasets based on the mode
  const dataSets = {
    datasets: isEventChart ? [
      {
        label: 'Ereignisse',
        pointBackgroundColor: (ctx: any) => {
          // Access the specific data object from the dataset
          const item = ctx.dataset.data[ctx.dataIndex];
          const typeStr = item?.label;

          if (typeStr === 'Messtermine') return '#f44336'; // Red
          if (typeStr === 'Wartungstermine') return '#2196f3'; // Blue
          if (typeStr === 'Katalystorwechseltermine') return '#4caf50'; // Green
          if (typeStr === 'Servicetermine') return '#ff9800'; // Orange
          return '#000000';
        },
        // Optional: match the border of the triangles to the fill color
        pointBorderColor: (ctx: any) => {
          const item = ctx.dataset?.data?.[ctx.dataIndex];
          const typeStr = item?.label;
          if (typeStr === 'Messtermine') return '#f44336';
          if (typeStr === 'Wartungstermine') return '#2196f3';
          if (typeStr === 'Katalystorwechseltermine') return '#4caf50';
          if (typeStr === 'Servicetermine') return '#ff9800';
          return '#000000';
        },
        pointRadius: 10,
        pointStyle: 'triangle',
        type: 'scatter',
        // Map labels to Y-lanes between 0 and 1
        data: data.map((d: any) => {
          if (d.label === 'Messtermine') d.y = 0.2;
          if (d.label === 'Wartungstermine') d.y = 0.4;
          if (d.label === 'Katalystorwechseltermine') d.y = 0.6;
          if (d.label === 'Servicetermine') d.y = 0.8;
          return { ...d, y: d.y };
        }),
      }
    ] : [
      {
        label,
        pointBackgroundColor: color,
        backgroundColor: color,
        pointRadius: pointSize,
        pointHitRadius: pointHitSize,
        type: 'scatter',
        data,
      },
      {
        label: limitLabel,
        type: 'line',
        fill: false,
        borderColor: color,
        borderWidth: 1,
        borderDash: [8, 4],
        pointStyle: 'line',
        data: limitData,
      }
    ]
  };

  const options = {
    maintainAspectRatio: false,
    legend: {
      position: 'top',
      labels: {
        usePointStyle: true,
        fontSize: 12,
        boxWidth: 6,
      },
    },
    tooltips: {
      callbacks: {
        // 1. Clear the title for everything to keep tooltips single-line/merged
        title: () => '',

        // 2. Unified Label Logic
        label: (tooltipItem: any, data: any) => {
          const dataset = data.datasets[tooltipItem.datasetIndex];
          const dateStr = tooltipItem.xLabel; // The date from the X-axis

          if (isEventChart) {
            const datapoint = dataset.data[tooltipItem.index];

            // Remove trailing 'e' (Messtermine -> Messtermin)
            let cleanLabel = datapoint.label || 'Ereignis';
            if (cleanLabel.endsWith('e')) {
              cleanLabel = cleanLabel.slice(0, -1);
            }

            // Result: "Messtermin am 12.08.2021"
            return `${cleanLabel} am ${dateStr}`;
          }

          // Logic for CO, NOx, and Temp charts
          // result: "125 mg/Nm³ am 12.08.2021"
          const unit = type === 'temp' ? '°C' : 'mg/Nm³';
          const value = tooltipItem.yLabel;

          return showHmw ? `${value} ${unit} um ${dateStr}` : `${value} ${unit} am ${dateStr}`;
        }
      }
    },
    scales: {
      xAxes: [{
        scaleLabel: {
          display: true,
          labelString: 'Datum',
        },
        bounds: 'ticks',
        type: 'time',
        ticks: {
          min: minDate,
          max: maxDate || Date.now(),
        },
        time: {
          autoSkip: false,
          unit: 'day',
          unitStepSize,
          tooltipFormat: showHmw ? 'HH:mm DD.MM' : 'DD.MM',
          displayFormats: {
            day: 'DD.MM.YYYY',
          },
        },
        position: 'bottom',
      }],
      yAxes: [{
        // display: !isEventChart,
        ticks: {
          min: 0,
          max: isEventChart ? 1 : undefined, // Keep events within the 0-1 range
          stepSize: isEventChart ? 1 : 100,
        },
        scaleLabel: {
          display: true,
          labelString: isEventChart ? '' : yAxisLabel,
        },
        gridLines: {
          display: false,
        },
      }],
    },
  };

  return (
    // @ts-ignore
    <Scatter
      data={dataSets}
      options={options}
      height={fullSize ? 400 : 500}
    />
  );
}

export default React.memo(Chart);
